import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { QRCodeSVG } from "qrcode.react";
import { api } from "../lib/api";
import { toast } from "sonner";

/**
 * QR Cards — Printable A4 sheet of per-guest RSVP QR codes.
 * Each card has the couple names, guest name, QR code, and short URL.
 * Print this from browser to physical wedding-card inserts.
 */
export default function GuestQRCards() {
  const { id } = useParams();
  const [inv, setInv] = useState(null);
  const [guests, setGuests] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const [invRes, gRes] = await Promise.all([
          api.get(`/invitations/${id}`),
          api.get(`/invitations/${id}/guests`),
        ]);
        setInv(invRes.data);
        setGuests(gRes.data);
      } catch {
        toast.error("Could not load QR cards");
      } finally {
        setLoading(false);
      }
    })();
  }, [id]);

  if (loading) return <div className="p-10 text-stone-500">Loading QR cards…</div>;
  if (!inv) return <div className="p-10">Invitation not found.</div>;

  const base = window.location.origin;
  const publicUrl = `${base}/i/${inv.slug}`;
  const couple = `${inv.data?.groom_name || "Groom"} & ${inv.data?.bride_name || "Bride"}`;
  const hashtag = inv.data?.hashtag || "";

  return (
    <div className="min-h-screen bg-stone-50" data-testid="qr-cards-page">
      {/* Toolbar — hidden on print */}
      <div className="print:hidden sticky top-0 z-10 bg-white border-b border-stone-200 px-6 py-4 flex flex-wrap items-center justify-between gap-3">
        <div>
          <div className="text-[10px] uppercase tracking-[0.3em] text-stone-500">Guest RSVP QR Cards</div>
          <div className="font-serif-display text-2xl">{couple} · {guests.length} cards</div>
        </div>
        <div className="flex gap-2">
          <a href={`/rsvps/${id}`} className="px-4 py-2 border border-stone-900 text-stone-900 text-xs uppercase tracking-[0.2em] hover:bg-stone-100">← Back to RSVPs</a>
          <button onClick={() => window.print()} data-testid="qr-print-btn" className="px-5 py-2 bg-stone-900 text-white text-xs uppercase tracking-[0.3em] hover:bg-stone-800">Print / Save PDF</button>
        </div>
      </div>

      {/* Tip banner */}
      <div className="print:hidden mx-6 mt-6 bg-amber-50 border border-amber-200 p-4 text-sm text-stone-700" data-testid="qr-help-banner">
        <strong>How to use:</strong> Print this sheet, cut along dotted lines, and tuck one QR card inside each wedding card. Guest scans → lands on their personalized RSVP page (pre-filled with their name &amp; email).
      </div>

      {/* Public URL card — for the main printed invitation */}
      <div className="print:break-after-page mx-6 mt-6 bg-white border-2 border-dashed border-stone-300 p-8 flex flex-col md:flex-row items-center gap-6" data-testid="qr-public-card">
        <div className="bg-white p-3 border border-stone-200">
          <QRCodeSVG value={publicUrl} size={180} level="H" includeMargin={false} />
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-[0.4em] text-stone-500 mb-2">General invitation · for all guests</div>
          <div className="font-serif-display text-4xl italic mb-2">{couple}</div>
          {hashtag && <div className="text-sm" style={{ color: "#B89B5E" }}>{hashtag}</div>}
          <div className="mt-4 text-sm text-stone-600">Scan to view invitation &amp; RSVP</div>
          <div className="mt-1 text-xs font-mono text-stone-500">{publicUrl}</div>
        </div>
      </div>

      {/* Per-guest QR cards */}
      {guests.length === 0 ? (
        <div className="mx-6 mt-8 p-10 text-center border-2 border-dashed border-stone-300 text-stone-500">
          <div className="font-serif-display text-2xl mb-2">No guest list yet.</div>
          <div>Go to RSVPs → add guests → come back here to print personalised QR cards.</div>
        </div>
      ) : (
        <div className="p-6 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 print:grid-cols-3 print:gap-3" data-testid="qr-cards-grid">
          {guests.map((g) => {
            const personalUrl = g.token ? `${base}/i/${inv.slug}?guest=${g.token}` : publicUrl;
            return (
              <div key={g.id} className="bg-white border-2 border-dashed border-stone-300 p-5 flex flex-col items-center text-center break-inside-avoid" data-testid={`qr-guest-${g.id}`}>
                <div className="text-[10px] uppercase tracking-[0.3em] text-stone-500 mb-2">Kindly RSVP</div>
                <div className="font-serif-display text-xl italic mb-3" style={{ color: "#B89B5E" }}>{couple}</div>
                <div className="bg-white p-2 border border-stone-200 mb-3">
                  <QRCodeSVG value={personalUrl} size={140} level="H" includeMargin={false} />
                </div>
                <div className="font-serif-display text-lg">{g.name}</div>
                {g.email && <div className="text-[11px] text-stone-500 mt-0.5 truncate max-w-full">{g.email}</div>}
                <div className="mt-3 text-[9px] uppercase tracking-[0.2em] text-stone-400">Scan with phone camera</div>
              </div>
            );
          })}
        </div>
      )}

      <style>{`
        @media print {
          body { background: #fff !important; }
          @page { margin: 12mm; }
        }
      `}</style>
    </div>
  );
}
